/**
 * Le compte-rendu d'un élève sous une séance passée — la ligne dépliée de l'ancienne table
 * `LivesHistory`, dans son ordre : l'élève (photo, nom, classe, e-mail, « 1er mois »,
 * « Rattrapage »), son score d'engagement et ses trois axes, la note et les étiquettes de Camille,
 * son commentaire.
 *
 * Deux gestes, comme l'ancienne ligne : l'élève ouvre sa fiche (`student-profil`), le compte-rendu
 * ouvre le rapport de la séance (`r`). Le score a maintenant ses propres pastilles dépliables :
 * toute la zone ne presse donc plus — « Voir le rapport » le dit en mots.
 */
import React from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Badge } from "../../atoms/Badge"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Satisfaction } from "../../atoms/Satisfaction"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { TutorEngagementBar } from "../../molecules/TutorEngagementBar"
import { type UITutorHistoryReport } from "../../../ports/primary/ui/types/tutorLives"

const TEXTS = {
    firstMonth: "1er mois",
    recovery: "Rattrapage",
    rating: "Note tuteur",
    openReport: "Voir le rapport",
    noComment: "-",
}

export type TutorHistoryReportProps = {
    report: UITutorHistoryReport
    onOpenStudent?: () => void
    onOpenReport?: () => void
    testID?: string
}

export function TutorHistoryReport(props: TutorHistoryReportProps) {
    const { report, onOpenStudent, onOpenReport, testID } = props
    const { student } = report

    const identity = (
        <Row gap="3" align="center">
            <Avatar uri={student.uri} name={student.name} size={40} />
            <Stack gap="0" style={styles.grow}>
                <Text size="text-base" weight="bold" color="text-primary" numberOfLines={1}>
                    {student.name}
                </Text>
                {student.className != null && (
                    <Text size="text-xs" color="text-muted">
                        {student.className}
                    </Text>
                )}
                {student.email != null && (
                    <Text size="text-xs" color="text-muted" numberOfLines={1}>
                        {student.email}
                    </Text>
                )}
            </Stack>
        </Row>
    )

    return (
        <Card background="surface-subtle" elevation="none" p="3" testID={testID}>
            <Stack gap="3">
                {onOpenStudent != null ? (
                    <Pressable onPress={onOpenStudent} accessibilityRole="button">
                        {identity}
                    </Pressable>
                ) : (
                    identity
                )}
                {(report.firstMonth === true || report.recovery === true) && (
                    <Row gap="2" style={styles.wrap}>
                        {report.firstMonth === true && <Badge text={TEXTS.firstMonth} variant="danger" />}
                        {report.recovery === true && <Badge text={TEXTS.recovery} variant="neutral" iconName="redo" />}
                    </Row>
                )}
                {report.engagement != null && <TutorEngagementBar engagement={report.engagement} />}
                <Stack gap="1">
                    <Text size="text-xs" color="text-muted">
                        {TEXTS.rating}
                    </Text>
                    <Satisfaction rating={report.rating ?? 0} size={16} />
                    {report.tags.length > 0 && (
                        <Row gap="1" style={styles.wrap}>
                            {report.tags.map((tag) => (
                                <Badge key={tag} text={tag} variant="neutral" />
                            ))}
                        </Row>
                    )}
                </Stack>
                <Text size="text-sm" color="text-primary">
                    {report.comment != null && report.comment !== "" ? report.comment : TEXTS.noComment}
                </Text>
                {onOpenReport != null && (
                    <Row>
                        <TextButton label={TEXTS.openReport} onPress={onOpenReport} size="sm" />
                    </Row>
                )}
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    grow: { flex: 1, flexShrink: 1 },
    wrap: { flexWrap: "wrap" },
}))
